<template>
    <v-card class="my-2" variant="outlined">
        <v-data-table
            :headers="tableHeaders"
            :items="tableItems"
            :footer-props="{'items-per-page-options': [50, 100]}"
            :custom-sort="customSort"
            :mobile-breakpoint="0"
            :hide-default-header="isMobile"
        >
            <template v-slot:item.tourName="{item}">
                <div class="font-weight-medium">
                    <div
                        v-if="$route.name === 'tourSearch'"
                        v-tooltip="item.tourName"
                        class="tour-name text-decoration-none cursor-pointer text-primary font-weight-medium text-truncate"
                        @click="goToTour(item)"
                    >
                        {{ item.tourName }}
                    </div>
                    <span v-else>{{ item.tourName }}</span>
                </div>
            </template>
            <template v-slot:item.duration="{item}">
                <span class="nowrap">
                    {{ item.duration.days }}
                    <v-icon size="small">mdi-weather-sunny</v-icon>
                    {{ `/ ${item.duration.nights}` }}
                    <v-icon size="small">mdi-weather-night</v-icon>
                </span>
            </template>
            <template v-slot:item.accommodation="{item}">
                <div v-if="item.accommodation">
                    {{ item.accommodation }}
                </div>
                <span v-else>-</span>
            </template>
            <template v-slot:item.services="{item}">
                <tour-included-entries :entries="item.services" />
            </template>
            <template v-slot:item.price="{item}">
                <v-btn color="primary" size="small" @click="action(() => selectForBooking(item))">
                    {{ label($filters.price(item.price)) }}
                </v-btn>
            </template>
            <template v-slot:item.mobile="{item}">
                <div class="py-2 d-flex justify-space-between">
                    <div>
                        <div class="font-weight-medium text-truncate">
                            <span
                                v-if="$route.name === 'tourSearch'"
                                class="text-decoration-none cursor-pointer text-primary font-weight-medium"
                                @click="goToTour(item)"
                            >
                                {{ item.tourName }}
                            </span>
                            <span v-else>{{ item.tourName }}</span>
                        </div>
                        <div v-if="item.accommodation">
                            {{ item.accommodation }}
                        </div>
                        <div v-if="item.roomType" class="text-grey text-caption font-weight-bold">
                            {{ item.roomType }}
                        </div>
                        <div v-if="item.mealType" class="text-grey text-caption font-weight-bold">
                            {{ item.mealType }}
                        </div>
                    </div>

                    <div class="d-flex flex-column text-right">
                        <tour-included-entries :entries="item.services" small class="text-right" />
                        <div>{{ item.startDate }}</div>
                        <div>
                            <span class="nowrap">
                                {{ item.duration.days }}
                                <v-icon size="small">mdi-weather-sunny</v-icon>
                                {{ `/ ${item.duration.nights}` }}
                                <v-icon size="small">mdi-weather-night</v-icon>
                            </span>
                        </div>
                        <v-btn
                            color="primary"
                            size="small"
                            class="mt-auto"
                            @click="action(() => selectForBooking(item))"
                        >
                            {{ label($filters.price(item.price)) }}
                        </v-btn>
                    </div>
                </div>
            </template>
        </v-data-table>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Setup, Vue} from 'vue-facing-decorator'
    import TourIncludedEntries from '~src/components/tours/snippets/TourIncludedEntries.vue'
    import HotelStars from '~src/components/hotels/snippets/hotelStars.src'

    @Component({
        components: {TourIncludedEntries, HotelStars},
    })
    export class TourOfferTableView extends Vue {
        @Prop({required: true}) offers
        @Setup(() => useBookingPriceButton().action) action
        @Setup(() => useBookingPriceButton().label) label

        async selectForBooking(val) {
            const offerKey = val.offerKey
            const selectedOfferKey = val.roomKey
            await toursRuntimeStore.SELECT_OFFER({offerKey, selectedOfferKey})
            await toursStore.ADD_TO_BASKET(offerKey)
            await this.$router.push({
                name: 'tourBooking',
                query: {offerKey},
            })
        }

        async goToTour(val) {
            const offerKey = val.offerKey
            const selectedOfferKey = val.roomKey
            await toursRuntimeStore.SELECT_OFFER({offerKey, selectedOfferKey})
            await this.$router.push({
                name: 'tour',
                query: val.query,
            })
        }

        customSort(items) {
            items.sort((a, b) => {
                if (a.startDate === b.startDate) {
                    if (a.duration.days - b.duration.days === 0) {
                        if (a.accommodation === b.accommodation) {
                            if (a.roomType === b.roomType) {
                                return a.mealType > b.mealType ? 1 : -1
                            } else return a.roomType > b.roomType ? 1 : -1
                        } else return a.accommodation > b.accommodation ? 1 : -1
                    } else return a.duration.days - b.duration.days
                } else return new Date(a.startDate) > new Date(b.startDate) ? 1 : -1
            })

            return items
        }

        get tableHeaders() {
            if (this.isMobile) return [{value: 'mobile'}]

            return [
                {
                    title: this.$t('tour_table_column.tour_name'),
                    align: 'start',
                    value: 'tourName',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.start_date'),
                    align: 'start',
                    value: 'startDate',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.duration'),
                    align: 'start',
                    value: 'duration',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.accommodation'),
                    align: 'start',
                    value: 'accommodation',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.room_type'),
                    align: 'start',
                    value: 'roomType',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.meal_type'),
                    align: 'start',
                    value: 'mealType',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.services'),
                    align: 'start',
                    value: 'services',
                    sortable: false,
                },
                {
                    title: this.$t('tour_table_column.price'),
                    align: 'start',
                    value: 'price',
                    sortable: false,
                },
            ]
        }

        get tableItems() {
            return this.offers.reduce((tableItems, tour) => {
                const hotelIndex = tour.entries.findIndex(entry => entry.type === 'ACCOMMODATION' && entry.obligatory)

                if (hotelIndex >= 0) {
                    tour.entries[hotelIndex].offers.forEach(hotelOffer => {
                        hotelOffer.rooms
                            .filter(room => !room.soldOut)
                            .forEach(room => {
                                const data = {
                                    query: {
                                        ...this.searchRequest,
                                        packageTourId: tour.info.id,
                                        startDateFrom: this.$dateFns.format(tour.info.packageDate),
                                    },
                                    offerKey: tour.offerKey,
                                    roomKey: room.groupedOffers[0].offerKey,
                                    tourName: tour.info.name,
                                    startDate: this.$filters.dateShortFormat(tour.info.packageDate),
                                    duration: {
                                        days: tour.info.packageDuration,
                                        nights: tour.info.nightsDuration,
                                    },
                                    accommodation: hotelOffer.name,
                                    roomType: room.name,
                                    mealType: room.mealTypeName,
                                    services: tour.entries,
                                    price: {
                                        ...tour.price,
                                        amount: tour.price.amount + room.deltaPrice.amount,
                                    },
                                }

                                tableItems.push(data)
                            })
                    })
                } else {
                    tableItems.push({
                        query: {
                            ...this.searchRequest,
                            packageTourId: tour.info.id,
                            startDateFrom: this.$dateFns.format(tour.info.packageDate),
                        },
                        offerKey: tour.offerKey,
                        tourName: tour.info.name,
                        startDate: this.$filters.dateShortFormat(tour.info.packageDate),
                        duration: {
                            days: tour.info.packageDuration,
                            nights: tour.info.nightsDuration,
                        },
                        accommodation: null,
                        roomType: '-',
                        mealType: '-',
                        services: tour.entries,
                        price: tour.price,
                    })
                }

                return tableItems
            }, [])
        }

        get searchRequest() {
            return toursStore.searchRequest
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default TourOfferTableView
</script>

<style scoped lang="scss">
    .tour-name {
        line-height: 1.5em;
        height: 3em;
        overflow: hidden;
        width: 70px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: unset !important;
    }
</style>
